import { useNavigate } from "react-router-dom";
import { Tag, Tooltip } from "antd";
import { Waves, Anchor } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import isAllowed from "../../utils/isAllowed";

/** Configuración de países y sus secciones.
 *  Para agregar un nuevo país en el futuro, solo añade un objeto a este array.
 */
interface CountrySection {
  permission: string;
  /** true = genera ruta navegable; false = no se muestra (aún sin datos) */
  available: boolean;
}

interface OceanCountry {
  key: string;
  label: string;
  flag: string;
  sections: {
    superficial: CountrySection;
    profundidad: CountrySection;
  };
}

const OCEAN_COUNTRIES: OceanCountry[] = [
  {
    key: "peru",
    label: "Perú",
    flag: "🇵🇪",
    sections: {
      superficial: { permission: "ui:newsletter:peru_1", available: true },
      profundidad: { permission: "ui:newsletter:peru_2", available: true },
    },
  },
  {
    key: "ecuador",
    label: "Ecuador",
    flag: "🇪🇨",
    sections: {
      superficial: { permission: "ui:newsletter:ecuador", available: true },
      profundidad: { permission: "ui:newsletter:ecuador_2", available: true },
    },
  },
  {
    key: "oman",
    label: "Omán",
    flag: "🇴🇲",
    sections: {
      superficial: { permission: "ui:newsletter:oman", available: true },
      profundidad: { permission: "ui:newsletter:oman_2", available: true },
    },
  },
  {
    key: "panama",
    label: "Panamá",
    flag: "🇵🇦",
    sections: {
      superficial: { permission: "ui:newsletter:panama", available: true },
      profundidad: { permission: "ui:newsletter:panama_2", available: true },
    },
  },
];

/**
 * Comprueba si el usuario tiene acceso a un permiso.
 * También acepta el slug legacy sin sufijo _1/_2 como fallback.
 * Ej: peru_1 → fallback a "peru", ecuador_1 → fallback a "ecuador"
 */
function hasPermission(userPerms: string[] | undefined, slug: string): boolean {
  const perms = userPerms ?? [];
  if (isAllowed({ permissions: perms, permission: slug })) return true;
  // Fallback legacy: "ui:newsletter:ecuador_1" → "ui:newsletter:ecuador"
  const legacySlug = slug.replace(/_[12]$/, "");
  if (legacySlug !== slug) {
    return !!isAllowed({ permissions: perms, permission: legacySlug });
  }
  return false;
}

export default function OceanReportsHub() {
  const navigate = useNavigate();
  const { rolePermissions } = useAuth();
  const perms = rolePermissions ?? [];

  // Filtrar países que el usuario puede ver (al menos la sección superficial)
  const visibleCountries = OCEAN_COUNTRIES.filter((c) =>
    hasPermission(perms, c.sections.superficial.permission)
  );

  return (
    <div style={{ padding: "8px 0" }}>
      {/* ── Encabezado ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 28,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1a1a2e" }}>
          Boletines Oceanográficos
        </h2>
        <Tag
          color="blue"
          style={{ borderRadius: 6, fontWeight: 600, fontSize: 12 }}
        >
          🌊 Copernicus Marine
        </Tag>
      </div>

      {/* ── Grid de tarjetas ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: 20,
        }}
      >
        {visibleCountries.map((country) => {
          const canSeeProfundidad =
            country.sections.profundidad.available &&
            hasPermission(perms, country.sections.profundidad.permission);

          return (
            <CountryCard
              key={country.key}
              country={country}
              canSeeProfundidad={canSeeProfundidad}
              onNavigate={(section) =>
                navigate(`/admin/reports/${country.key}/${section}`)
              }
            />
          );
        })}
      </div>

      {visibleCountries.length === 0 && (
        <div
          style={{
            textAlign: "center",
            color: "#8c8c8c",
            marginTop: 60,
            fontSize: 15,
          }}
        >
          <Waves size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p>No tienes acceso a ningún boletín oceanográfico.</p>
          <p style={{ fontSize: 13 }}>
            Contacta a tu administrador para solicitar acceso.
          </p>
        </div>
      )}
    </div>
  );
}

/* ── Sub-componente: tarjeta de país ── */
interface CountryCardProps {
  country: OceanCountry;
  canSeeProfundidad: boolean;
  onNavigate: (section: number) => void;
}

function CountryCard({ country, canSeeProfundidad, onNavigate }: CountryCardProps) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 14,
        border: "1px solid #e8ecf0",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        transition: "box-shadow 0.2s",
      }}
      onMouseEnter={(e) =>
        ((e.currentTarget as HTMLDivElement).style.boxShadow =
          "0 6px 20px rgba(0,0,0,0.12)")
      }
      onMouseLeave={(e) =>
        ((e.currentTarget as HTMLDivElement).style.boxShadow =
          "0 2px 8px rgba(0,0,0,0.06)")
      }
    >
      {/* Header de la tarjeta */}
      <div
        style={{
          background: "linear-gradient(135deg, #0f3460 0%, #16213e 100%)",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ fontSize: 24 }}>{country.flag}</span>
        <span
          style={{
            color: "#fff",
            fontWeight: 700,
            fontSize: 16,
            letterSpacing: 0.3,
          }}
        >
          {country.label}
        </span>
      </div>

      {/* Botones de sección */}
      <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Superficial — siempre visible si llegó hasta acá */}
        <SectionButton
          label="Boletín 1"
          description="Variables de superficie (SST, salinidad, corrientes)"
          icon={<Waves size={16} />}
          color="#1890ff"
          bgColor="#e6f7ff"
          borderColor="#91d5ff"
          onClick={() => onNavigate(1)}
        />

        {/* Profundidad — solo si tiene permiso y está disponible */}
        {canSeeProfundidad && (
          <SectionButton
            label="Boletín v3"
            description="Isolíneas, variables subsuperficiales (0–200 m)"
            icon={<Anchor size={16} />}
            color="#10239e"
            bgColor="#f0f5ff"
            borderColor="#adc6ff"
            onClick={() => onNavigate(2)}
          />
        )}
      </div>
    </div>
  );
}

/* ── Sub-componente: botón de sección ── */
interface SectionButtonProps {
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  borderColor: string;
  onClick: () => void;
}

function SectionButton({
  label,
  description,
  icon,
  color,
  bgColor,
  borderColor,
  onClick,
}: SectionButtonProps) {
  return (
    <Tooltip title={description} placement="right">
      <button
        onClick={onClick}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: "10px 14px",
          background: bgColor,
          border: `1px solid ${borderColor}`,
          borderRadius: 10,
          cursor: "pointer",
          textAlign: "left",
          transition: "all 0.15s ease",
          color,
          fontWeight: 600,
          fontSize: 14,
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.filter = "brightness(0.95)";
          (e.currentTarget as HTMLButtonElement).style.transform = "translateX(3px)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.filter = "brightness(1)";
          (e.currentTarget as HTMLButtonElement).style.transform = "translateX(0)";
        }}
      >
        <span style={{ flexShrink: 0 }}>{icon}</span>
        <span>{label}</span>
        <span style={{ marginLeft: "auto", opacity: 0.5, fontSize: 18 }}>›</span>
      </button>
    </Tooltip>
  );
}
